import { createContext, useContext, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

const ChannelContext = createContext(null);

const STORAGE_KEY = "luma.channel";
const VALID_CHANNELS = new Set(["all", "external_api", "manual"]);

export const CHANNELS = [
  { value: "all", label: "All sources" },
  { value: "external_api", label: "External API" },
  { value: "manual", label: "In studio" },
];

export function ChannelProvider({ children }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const [channel, setChannelState] = useState(() => {
    // URL params take priority, then localStorage, then default "all"
    const urlChannel = searchParams.get("channel");
    if (VALID_CHANNELS.has(urlChannel)) return urlChannel;
    const stored = localStorage.getItem(STORAGE_KEY);
    return VALID_CHANNELS.has(stored) ? stored : "all";
  });

  // Persist to localStorage whenever channel changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, channel);
  }, [channel]);

  // Sync FROM URL when the user navigates back/forward
  useEffect(() => {
    const urlChannel = searchParams.get("channel");
    const resolved = VALID_CHANNELS.has(urlChannel) ? urlChannel : "all";
    setChannelState(resolved);
  }, [searchParams]);

  const setChannel = (newChannel) => {
    setChannelState(newChannel);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (newChannel === "all") {
          next.delete("channel"); // keep URL clean when using default
        } else {
          next.set("channel", newChannel);
        }
        return next;
      },
      { replace: false } // push to history so back/forward works
    );
  };

  return (
    <ChannelContext.Provider value={{ channel, setChannel }}>
      {children}
    </ChannelContext.Provider>
  );
}

/** Returns the channel param value to pass to API calls, or undefined when "all". */
export function channelParam(channel) {
  return channel === "all" ? undefined : channel;
}

export const useChannel = () => useContext(ChannelContext);
